import React, { useEffect } from 'react';
// external imports
import {
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Input,
  Divider,
  Button,
  FormHelperText,
  Grid,
} from '@material-ui/core';
// internal imports
import { useSetState } from '~/app/Utility/customHooks';
// redux imports
import { useSelector, useDispatch } from 'react-redux';
import { actions as setupPageActions } from '~/app/redux/setupPages/duck';
import { formHasErrors } from '../databaseSetupHelper';

const UpsertDonee = props => {
  const dispatch = useDispatch();

  const [state, setState] = useSetState({
    inputErrors: {},
    isSubmitDisabled: false,
  });

  const { isEditingRecord, currentEditRecord } = useSelector(state => ({
    isEditingRecord: state.setupPageState.isEditingRecord,
    currentEditRecord: state.setupPageState.currentEditRecord,
  }));

  useEffect(() => {
    setState({
      isSubmitDisabled: formHasErrors(currentEditRecord, handleSetErrors),
    });
  }, [currentEditRecord]);

  const handleSetErrors = errors => {
    setState({ inputErrors: errors }); // set any errors to the local state
  };

  const handleInputChange = e => {
    const name = e.target.name;
    e.persist();
    dispatch(setupPageActions.updateCurrentEditRecord(e));
    // Clear out inputErrors if user is modifying field value
    const tempErrors = state.inputErrors;
    if (tempErrors[name]) {
      delete tempErrors[name];
      setState({
        inputErrors: tempErrors,
      });
    }
  };

  const handleUpsert = () => {
    props.handleUserDBUpsert(currentEditRecord);
  };

  const validateFields = () => {
    if (
      currentEditRecord.address1 == null ||
      currentEditRecord.address1 === '' ||
      currentEditRecord.postal_code == null ||
      currentEditRecord.postal_code === '' ||
      currentEditRecord.donee_name == null ||
      currentEditRecord.donee_name === '' ||
      currentEditRecord.city == null ||
      currentEditRecord.city === '' ||
      currentEditRecord.state_code == null ||
      currentEditRecord.state_code === ''
    ) {
      return false;
    }
    return true;
  };

  return (
    <div>
      <AppBar position="static">
        <Toolbar style={styles.toolbar}>
          {isEditingRecord ? 'Edit ' : 'Add New '}
          Donee
        </Toolbar>
      </AppBar>
      <form styles={styles.form}>
        <Grid
          container
          spacing={8}
          style={{ marginBottom: '2em', marginTop: '2em', textAlign: 'left' }}
        >
          <Grid item xs={8} style={styles.sectionHeading}>
            Donee Information
          </Grid>
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} shrink>
                Donee EIN
              </InputLabel>
              <Input
                id="txtEINIDUpsertDoneeEIN"
                name="ein"
                value={currentEditRecord.ein}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={state.inputErrors?.ein !== undefined}
                inputProps={{
                  maxLength: '9',
                }}
                disableUnderline
              />
              {state.inputErrors?.ein && <FormHelperText>{state.inputErrors.ein}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                Donee name
              </InputLabel>
              <Input
                id="txtNameControlUpsertDoneeName"
                name="donee_name"
                value={currentEditRecord.donee_name}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={
                  !currentEditRecord.donee_name ||
                  currentEditRecord.donee_name === '' ||
                  state.inputErrors?.donee_name !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
              {state.inputErrors?.donee_name && (
                <FormHelperText>{state.inputErrors.donee_name}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Divider style={{ marginTop: '1em' }} />

        <Grid
          container
          spacing={8}
          style={{ marginBottom: '2em', marginTop: '2em', textAlign: 'left' }}
        >
          <Grid item xs={8} style={styles.sectionHeading}>
            Address
          </Grid>
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                Address
              </InputLabel>
              <Input
                id="txtEINIDUpsertDoneeAddress"
                name="address1"
                value={currentEditRecord.address1}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={
                  !currentEditRecord.address1 ||
                  currentEditRecord.address1 === '' ||
                  state.inputErrors?.address1 !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                City
              </InputLabel>
              <Input
                id="txtNameControlUpsertDoneeCity"
                name="city"
                value={currentEditRecord.city}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={
                  !currentEditRecord.city ||
                  currentEditRecord.city === '' ||
                  state.inputErrors?.city !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
              {state.inputErrors?.city && <FormHelperText>{state.inputErrors.city}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} shrink>
                State
              </InputLabel>
              <Input
                id="txtNameControlUpsertDoneeState"
                name="state_code"
                value={currentEditRecord.state_code || ''}
                onChange={handleInputChange}
                style={styles.inputStyle}
                inputProps={{
                  maxLength: '2',
                }}
                error={!currentEditRecord.state_code || state.inputErrors?.state_code !== undefined}
                disableUnderline
              />
              <FormHelperText>{state.inputErrors?.state_code}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                Zip Code
              </InputLabel>
              <Input
                id="txtNameControlUpsertDoneeZipCode"
                name="postal_code"
                value={currentEditRecord.postal_code}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={
                  !currentEditRecord.postal_code ||
                  currentEditRecord.postal_code === '' ||
                  state.inputErrors?.postal_code !== undefined
                }
                inputProps={{
                  maxLength: '12',
                }}
                disableUnderline
              />
              {state.inputErrors?.postal_code && (
                <FormHelperText>{state.inputErrors.postal_code}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={8} style={styles.bottomButtons}>
          <Grid item xs={9}>
            <Button
              id="btnCancelAddUUpsertDonee"
              color="primary"
              onClick={props.handleCloseUpsertModal}
              style={styles.button}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              id="btnUpsertDonee"
              color="primary"
              style={styles.button}
              onClick={handleUpsert}
              disabled={!validateFields() || state.isSubmitDisabled}
            >
              {isEditingRecord ? 'Save' : 'Add'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

const styles = {
  toolbar: {
    backgroundColor: '#444A59',
  },
  sectionHeading: {
    color: '#1A173B',
    fontFamily: 'Roboto',
    fontSize: '21px',
    marginBottom: '1em',
    marginLeft: '1em',
  },
  form: {
    margin: '2em',
  },
  button: {
    width: '9em',
    fontSize: '14px',
    borderRadius: '4px',
    marginLeft: '2em',
  },
  labelStyles: {
    color: '#354052',
    textAlign: 'left',
    fontFamily: 'Roboto',
  },
  inputStyle: {
    width: '20vw',
    height: '3.5vh',
  },
  bottomButtons: {
    textAlign: 'right',
    padding: '0',
    marginRight: '2em',
    marginBottom: '2em',
    marginTop: '2em',
    flexWrap: 'nowrap',
  },
};

export default UpsertDonee;
